import type { CoinMarket, GlobalResponse } from 'coingecko-api-v3'
import { computed } from 'vue'
import { useCoinPriceAPIStore, useLocaleStore } from '@/stores'
import type { BaseCoinPrice } from '@/types'

/**
 * CoinGecko API 에서 가져온 coin price 정보를 저장하는 클래스
 * 7일간 최저가의 경우
 */
export class CoinGeckoCoinPrice implements BaseCoinPrice {
  id: string
  price: number
  low24h: number
  lowAll: number
  high24h: number
  highAll: number
  tradingVolume: number
  marketCapRank: number
  marketCap: number
  marketCapDominance: number
  turnoverRatioOfMarketValue: number
  low7d!: number
  high7d!: number
  percent24h: number
  dailyRange?: number

  constructor(cgPrice: CoinMarket, globalRes: GlobalResponse) {
    this.id = cgPrice.id!
    this.price = cgPrice.current_price!
    this.low24h = cgPrice.low_24h!
    this.lowAll = cgPrice.atl!
    this.high24h = cgPrice.high_24h!
    this.highAll = cgPrice.ath!
    this.tradingVolume = cgPrice.total_volume!
    this.marketCapRank = cgPrice.market_cap_rank!
    this.marketCap = cgPrice.market_cap!
    this.dailyRange = Number(
      cgPrice.price_change_percentage_24h?.toFixed(1) ?? 0,
    )
    /**
     * 특정 coin 의 dominance 를 가져오는 coingecko api 가 존재하지 않음
     * 때문에 전체 market 의 dominance 를 가져와서 그 중에서 특정 coin 의 dominance 를 가져옴
     */
    this.marketCapDominance =
      globalRes.data.market_cap_percentage[cgPrice.symbol ?? '']
    this.turnoverRatioOfMarketValue =
      (this.tradingVolume ?? 0) / (this.marketCap ?? 1)
    this.percent24h = cgPrice.price_change_percentage_24h!

    const coinPriceAPIStore = useCoinPriceAPIStore()
    const computedLow7d = computed(() => {
      const chart7d = coinPriceAPIStore
        .getChart7d(this.id)
        ?.prices.map((p) => p[1])
      if (!chart7d) return 0
      return Math.min(...chart7d)
    })
    Object.defineProperty(this, 'low7d', {
      get(): number {
        return computedLow7d.value
      },
    })
    const computedHigh7d = computed(() => {
      const chart7d = coinPriceAPIStore
        .getChart7d(this.id)
        ?.prices.map((p) => p[1])
      if (!chart7d) return 0
      return Math.max(...chart7d)
    })
    Object.defineProperty(this, 'high7d', {
      get(): number {
        return computedHigh7d.value
      },
    })
  }

  async updateLowdHigh7d() {
    const localeStore = useLocaleStore()
    const coinPriceAPIStore = useCoinPriceAPIStore()
    coinPriceAPIStore.fetchChart7d({
      coinGeckoId: this.id,
      currency: localeStore.localeInfo!.currency,
    })
  }
}
